import React, { useState, useEffect } from 'react';
import { CheckCircle2, Layers, ArrowRight, Zap, Target, Layout, Menu, X, Activity, Lock } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;
  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  // Add scroll listener for the glassmorphism effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isOpen ? 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-white border-b border-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto w-full px-6 h-20 flex justify-between items-center">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 cursor-pointer group">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm transition-transform group-hover:scale-105"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "For Coaches"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors">
            Login
          </a>
          <a href="/create-account" className="px-5 py-2 bg-slate-900 text-white text-sm font-bold rounded-lg hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20">
            Create Account
          </a>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2 text-slate-600" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="absolute top-20 left-0 w-full bg-white border-b border-slate-100 p-6 flex flex-col gap-4 md:hidden shadow-xl animate-in slide-in-from-top-5 z-40">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-semibold ${
                  link.name === "For Coaches" ? "text-blue-600" : "text-slate-900"
                }`}
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a href="/login" className="text-lg font-semibold text-slate-900">Login</a>
          <a href="/create-account" className="w-full text-center py-4 bg-slate-900 text-white font-bold rounded-xl">
            Deploy System
          </a>
        </div>
      )}
    </nav>
  );
};

const Hero = () => (
  <section className="bg-white pt-32 md:pt-40 pb-16 md:pb-24 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[11px] font-bold uppercase tracking-widest mb-8">
        <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
        The Infrastructure of Winning
      </div>

      <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-8 leading-[1.1]">
        Don't Rebuild. <br />
        <span className="text-blue-600">Reload.</span>
      </h1>

      <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
        Dynasties aren't built on luck; they are built on standards.
        Nuvio is the operating system that aligns your entire program—from youth feeders to varsity starters—to <strong>your specific DNA</strong>.
      </p>

      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <a href="/create-account" className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/20">
          Build Your Standard <ArrowRight size={18} />
        </a>
        <a href="https://calendar.app.google/919vQjWVJnBKRPgA8" className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 border-2 border-slate-100 font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center">
          Schedule a Demo
        </a>
      </div>
    </div>
  </section>
);

const ProblemSolution = () => (
  <section className="py-20 md:py-24 px-6 bg-slate-50">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

      {/* The Problem (The Villain) */}
      <div className="order-2 lg:order-1">
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">Talent is ever Fluctuating. <br />Systems are Forever.</h2>
        <p className="text-slate-600 text-lg mb-6 leading-relaxed">
          Most programs are at the mercy of the talent cycle. One year you have a generational group; the next, you're starting from scratch.
        </p>
        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
          Worse, the offseason is a relative <strong>"unknown."</strong> Players go to different gyms, training under different philosophies, developing habits that don't always fit your scheme.
        </p>
        <div className="p-6 bg-white rounded-xl border-l-4 border-red-500 shadow-sm">
          <p className="font-bold text-slate-900 mb-1">The Cost of the Unknown:</p>
          <p className="text-slate-500">You spend the first month of the season retraining habits instead of installing your offense.</p>
        </div>
      </div>

      {/* The Solution (The Hero) */}
      <div className="order-1 lg:order-2 bg-white p-8 md:p-10 rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100">
        <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><Layers size={24} /></div>
          The Alignment Engine
        </h3>
        <ul className="space-y-8">
          {[
            { icon: <Lock size={20} />, title: "Codify Your DNA", desc: "Upload your terminology and drills. You define exactly what a 'Varsity Standard' looks like." },
            { icon: <Target size={20} />, title: "Vertical Integration", desc: "Push your system down to the youth levels. Build a pipeline that speaks your language." },
            { icon: <Zap size={20} />, title: "Rapid Deployment", desc: "We align your entire roster in minutes. Focus on coaching, not managing software." }
          ].map((feature, i) => (
            <li key={i} className="flex gap-4 group">
              <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 transition-colors shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-lg">{feature.title}</h4>
                <p className="text-slate-500 leading-snug">{feature.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="protocol" className="py-24 px-6 bg-white scroll-mt-24">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-20">
        <h2 className="text-3xl font-extrabold text-slate-900 mb-4">The Protocol</h2>
        <p className="text-slate-500 text-lg">Three steps to a self-sustaining dynasty.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8 md:gap-12">
        {[
          { step: "01", title: "Define The Standard", desc: "You don't need another drill library. You need YOUR library. Input the skills that matter to your system." },
          { step: "02", title: "Automate The Grind", desc: "The system assigns daily, individualized development plans to every athlete based on their role and level." },
          { step: "03", title: "Verify The Work", desc: "Trust is good; data is better. See exactly who is investing in the program and who is falling behind." }
        ].map((item, i) => (
          <div key={i} className="relative p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all group overflow-hidden">
            <div className="text-8xl font-black text-white absolute -top-4 -right-4 group-hover:text-blue-100/50 transition-colors select-none">{item.step}</div>
            <div className="relative z-10">
              <div className="w-12 h-1 bg-blue-600 mb-6"></div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed font-medium">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturePreview = () => (
  <section className="py-20 px-6 bg-slate-50">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">Features</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Built for program-wide execution</h2>
          <p className="text-slate-600 mt-3 max-w-2xl">
            From SWOT priorities to skill workouts, Nuvio aligns every athlete to the same standard.
          </p>
        </div>
        <a
          href="/home/features"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-700 font-bold hover:bg-slate-100 transition-all"
        >
          View All Features <ArrowRight size={16} />
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">SWOT & Priorities</h3>
          <p className="text-sm text-slate-600">
            Set what matters most and keep every athlete aligned to those priorities.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Skill + S&amp;C Workouts</h3>
          <p className="text-sm text-slate-600">
            Assign development plans and track progress across the roster.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Tryouts & Scheduling</h3>
          <p className="text-sm text-slate-600">
            Organize tryouts and events with clear visibility for staff and athletes.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-24 px-6 bg-slate-900 text-white text-center">
    <div className="max-w-3xl mx-auto">
      <h2 className="text-4xl md:text-5xl font-extrabold mb-8 tracking-tight">Own Your Success.</h2>
      <p className="text-slate-400 mb-12 text-xl leading-relaxed">
        Elite programs don't guess. They execute. <br />
        Join the programs who have stopped rebuilding and started reloading.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <a href="/create-account" className="w-full sm:w-auto px-10 py-4 bg-blue-600 font-bold rounded-xl hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/50 text-lg">
          Start The Nuvio Journey
        </a>
        <a href="https://calendar.app.google/919vQjWVJnBKRPgA8" className="w-full sm:w-auto px-10 py-4 bg-white/10 font-bold rounded-xl hover:bg-white/20 transition-all text-lg backdrop-blur-sm border border-white/10">
          Schedule a Demo
        </a>
      </div>
    </div>
  </section>
);

export default function ProgramLanding() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-600 selection:text-white">
      <Navbar />
      <Hero />
      <ProblemSolution />
      <HowItWorks />
      <FeaturePreview />
      <CTA />
    </div>
  );
}
